/**
 * Minimal HTTP helpers for Firebase HTTPS Callable-style endpoints.
 * Split from client.ts so login screens do not pull the full API surface into the initial bundle.
 */

export const FUNCTIONS_URL = 'https://europe-west1-luria-hours.cloudfunctions.net';

export const REQUEST_TIMEOUT_MS = 12000;

export type FunctionResult = {
  success: boolean;
  error?: string;
  [key: string]: any;
};

/**
 * Generic helper to call a Firebase HTTPS function with a JSON body and timeout.
 */
export async function callFunction<T extends FunctionResult>(
  endpoint: string,
  body: any,
): Promise<T> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

  try {
    const response = await fetch(`${FUNCTIONS_URL}/${endpoint}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
      signal: controller.signal,
    });

    const data = await response.json();
    return data;
  } catch (error: any) {
    if (error?.name === 'AbortError') {
      console.error(`[API] Request to ${endpoint} timed out after ${REQUEST_TIMEOUT_MS}ms`, error);
      return {
        success: false,
        error: 'הבקשה לשרת לקחה יותר מדי זמן. אנא נסה שוב בעוד מספר שניות.',
      } as T;
    }

    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * POST JSON to a Firebase HTTPS function with the same timeout semantics as {@link callFunction}.
 */
export async function postJsonWithTimeout<T extends FunctionResult>(
  endpoint: string,
  body: Record<string, unknown>,
): Promise<T> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

  try {
    const response = await fetch(`${FUNCTIONS_URL}/${endpoint}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
      signal: controller.signal,
    });

    const data = await response.json();
    return data as T;
  } catch (error: any) {
    if (error?.name === 'AbortError') {
      console.error(`[API] Request to ${endpoint} timed out after ${REQUEST_TIMEOUT_MS}ms`, error);
      return {
        success: false,
        error: 'הבקשה לשרת לקחה יותר מדי זמן. אנא נסה שוב בעוד מספר שניות.',
      } as T;
    }

    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
}
